import { useNavigate, useParams } from '@tanstack/react-router';

import type { Post } from '@/entities/post/post-types';
import { requestSaveToBoardsDialog } from '@/features/boards';
import { FlowerSpinner } from '@/shared/components/loader/flower-spinner';
import { Stage } from '@/shared/components/stage/stage';
import { usePost } from '../../../entities/post/query/use-post';
import { OriginalContents } from './ui';
import { PostMediaContainer } from './ui';
import { PostHeader } from './ui';
import { PostPerformance } from './ui';
import { PostProfile } from './ui';
import { VideoAnalysis } from './ui';
import { VideoSummary } from './ui';

export const PostDetailPage = () => {
  const { workspaceId, postId } = useParams({
    from: '/workspace/$workspaceId/post/$postId',
  });
  const navigate = useNavigate({ from: '/workspace/$workspaceId/post/$postId' });
  const { data: post, isLoading, refetch } = usePost(postId);

  return (
    <Stage className="flex justify-center">
      <Stage.Scene status={isLoading} className="flex-center h-full">
        <FlowerSpinner />
      </Stage.Scene>
      <Stage.Scene data={post}>
        {(post) => (
          <PostDetailPageContent
            post={post}
            onSavedToBoard={refetch}
            onOpenBoard={(boardId) =>
              navigate({
                to: '/workspace/$workspaceId/boards/$boardId',
                params: { workspaceId, boardId },
              })
            }
          />
        )}
      </Stage.Scene>
    </Stage>
  );
};

const PostDetailPageContent = ({
  post,
  onSavedToBoard,
  onOpenBoard,
}: {
  post: Post;
  onSavedToBoard: () => Promise<unknown>;
  onOpenBoard: (boardId: string) => void;
}) => {
  return (
    <div className="flex w-full flex-col pb-100 xl:max-w-300">
      <div className="px-6 py-7.5">
        <PostHeader
          post={post}
          onSaveToList={async () => {
            await requestSaveToBoardsDialog({
              postIds: [post._id],
              onSaved: async () => {
                await onSavedToBoard();
              },
            });
          }}
          savedBoards={post.boards ?? []}
          onOpenBoard={onOpenBoard}
        />
      </div>
      <div className="p-6 pt-0">
        <PostPerformance post={post} />
      </div>
      <div className="flex gap-6 px-6">
        <div className="flex w-60 flex-col gap-6">
          <PostMediaContainer post={post} />
        </div>
        <div className="flex flex-1 flex-col gap-4">
          <VideoSummary post={post} />
          <VideoAnalysis post={post} />
          <OriginalContents post={post} />
          <PostProfile post={post} />
        </div>
      </div>
    </div>
  );
};
